import { Children, FC, PropsWithChildren, ReactNode } from "react";

import { Box, Divider, Stack } from "@mui/material";

import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Add } from "@/icons/outlined";

interface QuestionSectionProps {
  title: string;
  addButtonLabel: string;
  onAddQuestion: () => void;
  addButtonTestId?: string;
  titleTooltip?: string;
  emptyState?: ReactNode;
  vibe?: "neutral" | "brand";
}

export const QuestionSection: FC<PropsWithChildren<QuestionSectionProps>> = ({
  title,
  addButtonLabel,
  onAddQuestion,
  addButtonTestId,
  titleTooltip,
  emptyState,
  vibe = "neutral",
  children,
}) => {
  const hasQuestions = Children.toArray(children).length > 0;

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <DashboardTypography variant="subtitle2" sx={{ color: "text.neutral.quiet", textTransform: "uppercase" }}>
              {title}
            </DashboardTypography>
            {titleTooltip && (
              <DashboardInformationTooltip
                title={titleTooltip}
                placement="top"
                slotProps={{ icon: { size: "small" } }}
              />
            )}
          </Stack>
          <Divider sx={{ flex: 1, borderColor: `border.${vibe}.quiet` }} />
        </Stack>
        {emptyState}
      </Stack>

      {hasQuestions && <Stack spacing={1}>{children}</Stack>}

      <Box>
        <DashboardButton
          variant="outlined"
          vibe="brand"
          startIcon={<Add />}
          onClick={onAddQuestion}
          data-test={addButtonTestId}
        >
          {addButtonLabel}
        </DashboardButton>
      </Box>
    </Stack>
  );
};
